import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  getPendingInvitations,
  acceptInvitation,
  rejectInvitation,
  revokeInvitation,
  Invitation,
} from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { Mail, CheckCircle, XCircle, X, Clock, AlertCircle } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';

const InvitationsList: React.FC = () => {
  const navigate = useNavigate();
  const { refreshTeams, refreshInvitations } = useArtist();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get role_id from sessionStorage
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole');
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();

  if (systemRole !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPendingInvitations();
      setInvitations(data);
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      setError(err.message || 'Failed to fetch invitations');
      toast.error(err.message || 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation: Invitation) => {
    if (!invitation.invitationToken) {
      toast.error('Invitation token not available. Please use the link from your email.');
      return;
    }
    try {
      await acceptInvitation(invitation.invitationToken, false);
      toast.success('Invitation accepted successfully');
      
      // Refresh teams to update available artists in sidebar
      await refreshTeams();
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      
      fetchInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Failed to accept invitation');
    }
  };

  const handleReject = async (invitation: Invitation) => {
    if (!invitation.invitationToken) {
      toast.error('Invitation token not available. Please use the link from your email.');
      return;
    }
    if (!window.confirm('Are you sure you want to reject this invitation?')) return;
    try {
      await rejectInvitation(invitation.invitationToken);
      toast.success('Invitation rejected');
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      fetchInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject invitation');
    }
  };

  const handleRevoke = async (invitationId: number) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return;
    try {
      await revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      fetchInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Failed to revoke invitation');
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const isExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
  };

  // Helper function to get team status badge
  const getTeamStatusBadge = (teamIsActive: boolean | undefined) => {
    if (teamIsActive === undefined || teamIsActive === null) {
      return null; // Status unknown
    }
    if (teamIsActive === true) {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-family-messina-sans flex items-center gap-1">
          <CheckCircle size={12} />
          Team Active
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-family-messina-sans flex items-center gap-1">
          <AlertCircle size={12} />
          Team Inactive
        </span>
      );
    }
  };

  // Helper function to render View Team button based on status
  const renderViewTeamButton = (invitation: Invitation) => {
    const isTeamActive = invitation.teamIsActive === true;
    
    if (isTeamActive) {
      return (
        <button
          onClick={() => navigate(`/teams/${invitation.teamId}`)}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
        >
          View Team
        </button>
      );
    } else {
      // Team is inactive or deleted
      const statusText = invitation.teamIsActive === false ? 'Team is Inactive' : 'Team Deleted';
      return (
        <button
          disabled
          className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed text-sm font-family-messina-sans opacity-60"
          title={statusText}
        >
          {statusText}
        </button>
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Invitations
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Invitations
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem] mb-6">
          Invitations
        </span>

        {invitations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="text-gray-600 font-family-messina-sans">
              No pending invitations
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {invitations.map((invitation) => (
              <div
                key={invitation.invitationId}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                        {invitation.teamName}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded text-xs font-family-messina-sans ${
                          invitation.invitationStatus === 'pending'
                            ? isExpired(invitation.expiresAt)
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                            : invitation.invitationStatus === 'accepted'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {invitation.invitationStatus === 'pending' && isExpired(invitation.expiresAt)
                          ? 'Expired'
                          : invitation.invitationStatus}
                      </span>
                      {getTeamStatusBadge(invitation.teamIsActive)}
                    </div>
                    <p className="text-sm text-gray-600 font-family-messina-sans mb-2">
                      <span className="font-medium">Artist:</span> {invitation.artistName}
                    </p>
                    <p className="text-sm text-gray-600 font-family-messina-sans mb-2">
                      <span className="font-medium">Invited by:</span> {invitation.inviterName}
                    </p>
                    {invitation.message && (
                      <p className="text-sm text-gray-600 font-family-messina-sans mb-2 italic">
                        "{invitation.message}"
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {invitation.roles.map((role) => (
                        <span
                          key={role}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-family-messina-sans"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 font-family-messina-sans">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>Sent: {formatDate(invitation.sentAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>Expires: {formatDate(invitation.expiresAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Only show Accept/Reject buttons if team is active and invitation is pending */}
                    {invitation.invitationStatus === 'pending' && !isExpired(invitation.expiresAt) && invitation.teamIsActive === true && (
                      <>
                        <button
                          onClick={() => handleAccept(invitation)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                        >
                          <CheckCircle size={16} />
                          <span>Accept</span>
                        </button>
                        <button
                          onClick={() => handleReject(invitation)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                        >
                          <XCircle size={16} />
                          <span>Reject</span>
                        </button>
                      </>
                    )}
                    {invitation.invitationStatus === 'pending' && (
                      <button
                        onClick={() => handleRevoke(invitation.invitationId)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                      >
                        <X size={16} />
                        <span>Revoke</span>
                      </button>
                    )}
                    {renderViewTeamButton(invitation)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationsList;

